import React, { createContext, useContext, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { fetchMappingData } from '../../../store/reducers/feeManagement/feeManagementSlice'
import {
  selectMappingData,
  selectMappingDataLoading,
  selectMappingDataError,
} from '../../../store/reducers/feeManagement/feeManagementSelectors'
import { ALL_ENTITY_TYPES } from '../constants/OverrideTypes'

// Create the context
const GridDataContext = createContext(null)

// Custom hook to use the context
export const useGridData = () => {
  const context = useContext(GridDataContext)
  if (!context) {
    throw new Error('useGridData must be used within a GridDataProvider')
  }
  return context
}

// Provider component
export const GridDataProvider = ({ children }) => {
  const dispatch = useDispatch()

  // Get mapping data from Redux state using selectors
  const mappingData = useSelector(selectMappingData)
  const isLoadingMappingData = useSelector(selectMappingDataLoading)
  const mappingDataError = useSelector(selectMappingDataError)

  // Function to fetch mapping data based on entity types
  const fetchMappingDataFn = useCallback(
    async (entityTypes) => {
      if (!entityTypes || !entityTypes.length) {
        return
      }

      dispatch(fetchMappingData(entityTypes))
    },
    [dispatch],
  )

  // Automatically fetch all mapping data when the provider mounts
  useEffect(() => {
    // Fetch all entity types to ensure we have complete data
    // Remove duplicates if any
    const uniqueEntityTypes = [...new Set(ALL_ENTITY_TYPES)]

    // Only fetch if we don't already have the data
    if (
      !mappingData ||
      !mappingData.products ||
      !Array.isArray(mappingData.products) ||
      mappingData.products.length === 0 ||
      Object.keys(mappingData).length === 0
    ) {
      fetchMappingDataFn(uniqueEntityTypes)
    }
  }, [fetchMappingDataFn, mappingData])

  // Expose the context value
  const value = {
    mappingData,
    isLoadingMappingData,
    mappingDataError,
    fetchMappingData: fetchMappingDataFn,
  }

  return <GridDataContext.Provider value={value}>{children}</GridDataContext.Provider>
}

GridDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default GridDataContext
